<template>
  <Table
    ref="table"
    service="Payroll"
    :min-width="groupByCampaign ? '1472px' : '1272px'"
    :initialize="initialize"
    :exportData="exportData"
    :params="params"
    :headers="headers"
    :messages="messages"
    :show-select="false"
    :show-filter-button="false"
    :no-edit="true"
    :show-actions="actions"
    :row-class="payslipRowClass"
    @custom="lockPayroll"
    customlabel="Bloquear"
  >
    <template v-slot:submenu-middle>
      <h4 v-if="payrollId > 0" class="mr-10">Fecha de bloqueo: {{ payrollDate }} </h4>
      <h4 v-if="payrollId > 0" class="mr-10">Importe total: {{ payrollTotal }} </h4>
      <v-chip
        v-for="chip in incidentChips"
        :key="chip.type"
        class="mr-2"
        :color="chip.color"
        small
        label
        @click="openIncidents( chip.type )"
      >
        {{ chip.text }}
      </v-chip>
      <v-switch
        v-model="groupByCampaign"
        class="mt-0 mr-10 pt-0"
        label="Agrupar por campaña"
        hide-details
        dense
      />
      <DChangeMonth
        v-model="month"
      />
    </template>

    <template v-slot:item.workerCount="{ value, item }">
      <Btn
        :color="'primary'"
        @click="showWorkerLines( item )"
        small
      >
        {{ value }}
      </Btn>
    </template>

    <template v-slot:item.incentiveCount="{ value, item }">
      <Btn
        :color="'primary'"
        @click="showIncentiveLines( item )"
        small
      >
        {{ value }}
      </Btn>
    </template>
    
    <!-- Where Bloquear sits: a month is either open and can be closed, or the last closed and can
         be reopened, never both. -->
    <template v-slot:actions-end>
      <Btn v-if="canUnlock" class="ml-2" color="tertiary" dark @click="unlockPayroll">
        Desbloquear
      </Btn>
    </template>

    <template v-slot:append-outer>
      <v-dialog
        v-model="dialog"
        max-width="95vw"
      >
        <Card v-if="incentiveDialog" class="incentives-dialog">

          <Toolbar color="secondary" dark>

            <h3>Listado de Incentivos</h3>

            <v-spacer/>

            <Btn color="white" class="mr-5" width="80" height="32" light @click="exportData('incentiveLines')">
              Exportar
            </Btn>

            <Btn color="white" width="20" height="20" fab light @click="closeDialog">
              <v-icon small>$close</v-icon>
            </Btn>

          </Toolbar>

          <div class="incentives-dialog-body">
            <DTable
              :items="incentivesList"
              :headers="incentivesHeaders"
              min-width="1300px"
              :row-class="rowClass"
              :border="false"
              :multiple-sort="false">
            </DTable>
          </div>

        </Card>

        <Card v-else class="workers-dialog">

          <Toolbar color="secondary" dark>

            <h3>Listado de Jornadas</h3>

            <v-spacer/>

            <Btn color="white" class="mr-5" width="80" height="32" light @click="exportData('workerLines')">
              Exportar
            </Btn>

            <Btn color="white" width="20" height="20" fab light @click="closeDialog">
              <v-icon small>$close</v-icon>
            </Btn>

          </Toolbar>

          <div class="workers-dialog-body">
            <DTable
              :items="workersList"
              :headers="workersHeaders"
              min-width="1400px"
              :row-class="workerRowClass"
              :border="false"
              :multiple-sort="false">
              <template v-slot:item.holiday="{ item }">
                <div>{{item.holiday ? "Sí": "No"}}</div>
              </template>
            </DTable>
          </div>

        </Card>
      </v-dialog>

      <v-dialog
        v-model="incidentsDialog"
        max-width="95vw"
        :persistent="!!closingReview"
      >
        <Card class="incidents-list-dialog">

          <Toolbar color="secondary" dark>

            <h3 v-if="closingReview">Incidencias antes de bloquear la nómina</h3>
            <h3 v-else>Incidencias de la nómina</h3>

            <v-spacer/>

            <Btn color="white" class="mr-5" width="80" height="32" light
              :disabled="! incidentRows.length" @click="exportIncidents">
              Exportar
            </Btn>

            <Btn color="white" width="20" height="20" fab light @click="closeIncidents">
              <v-icon small>$close</v-icon>
            </Btn>

          </Toolbar>

          <div class="pa-4 pb-2">
            <p v-if="closingReview && closingReview.changed" class="red--text mb-2">
              <strong>Las incidencias han cambiado desde que se mostraron.</strong>
              Revíselas de nuevo antes de aceptar.
            </p>
            <p v-if="closingReview" class="mb-2">
              Si acepta, la nómina se bloqueará con estas incidencias. Las jornadas sin validar
              quedarán bloqueadas y ya no se podrán validar, y las jornadas sin estampar se
              estamparán con la tarifa actual de su asignación.
            </p>
            <p v-else-if="!isOpen" class="mb-2">
              La nómina está bloqueada: estas jornadas no se validaron y ya no se pueden validar.
            </p>
            <v-chip
              v-for="chip in incidentFilterChips"
              :key="chip.type || 'all'"
              class="mr-2 mb-1"
              :color="chip.color"
              :outlined="incidentsFilter !== chip.type"
              small
              label
              @click="incidentsFilter = chip.type"
            >
              {{ chip.text }}
            </v-chip>
          </div>

          <div class="incidents-list-dialog-body">
            <DTable
              :items="incidentRows"
              :headers="incidentHeaders"
              min-width="1500px"
              :row-class="incidentRowClass"
              :border="false"
              :multiple-sort="false">
            </DTable>
          </div>

          <div v-if="closingReview" class="d-flex justify-end pa-4">
            <Btn class="mr-4" color="secondary" text @click="closeIncidents">
              Cancelar
            </Btn>
            <Btn color="primary" :loading="closing" @click="acceptIncidents">
              Aceptar y bloquear
            </Btn>
          </div>

        </Card>
      </v-dialog>
    </template>

  </Table>
</template>

<script>
import Table from "@/components/core/Table.vue";
import moment from "moment";
import { formatDate, saveFile } from "@/utils";
import { KpiTypes } from '@/utils/constants';
import { mapMutations, mapState } from 'vuex';

// What each kind of incident is called and the colour it is shown in, both in the list and on
// the payroll lines and days that have it. INCIDENT_ORDER is the priority when a row has more
// than one: what the payroll would pay wrongly comes first. Mirrors WSPayrollIncident.Type.
const INCIDENTS = {
  MISSING_RATE: { label: 'Sin tarifa', color: 'red lighten-4', cls: 'incident-missing-rate', count: 'missingRate' },
  RATE_CHANGED: { label: 'Tarifa cambiada', color: 'orange lighten-4', cls: 'incident-rate-changed', count: 'rateChanged' },
  NOT_STAMPED: { label: 'Sin estampar', color: 'blue lighten-4', cls: 'incident-not-stamped', count: 'notStamped' },
  VALIDATED_AT_ZERO: { label: 'Validada a cero', color: 'yellow lighten-3', cls: 'incident-validated-zero', count: 'validatedAtZero' },
  NOT_VALIDATED: { label: 'Sin validar', color: 'purple lighten-4', cls: 'incident-not-validated', count: 'notValidated' }
};
const INCIDENT_ORDER = [ 'MISSING_RATE', 'RATE_CHANGED', 'NOT_STAMPED', 'VALIDATED_AT_ZERO', 'NOT_VALIDATED' ];
const ACTIVITY = { RECORDED: 'con actividad', PLANNED: 'solo prevista', NONE: 'vacía' };
const MONTH_NAMES = [ 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre' ];

// COMMON headers

export default {
  components: { Table },
  data() {
    const date = moment().startOf('month').valueOf();
    return {
      messages: { item: "Nómina" },
      month: date,
      groupByCampaign: false,
      dialog: false,
      incentiveDialog: false,
      item: null,
      workersList: [],
      workersHeaders: [
        { value: 'day', text: 'Fecha' },
        { value: 'campaignName', text: 'Campaña', width: 250, },
        { value: 'holiday', text: 'Festivo', width: 80 },
        { value: 'validatedWorkingTime', text: 'Tiempo validado (mins)' },
        { value: 'costHour', text: 'Coste / hora' },
        { value: 'amount', text: 'Importe laborables' },
        { value: 'extraCostDay', text: 'Plus jornada' },
        { value: 'holidayCostHour', text: 'Coste / hora festivo' },
        { value: 'holidayAmount', text: 'Importe festivos' },
        { value: 'paidHolidaysAmount', text: 'Importe vacaciones' },
        { value: 'validatedVisits', text: 'Visitas validadas' },
        { value: 'costVisit', text: 'Coste / visita' },
        { value: 'visitAmount', text: 'Importe visitas' },
        { value: 'total', text: 'Total' }
      ],
      incentivesList: [],
      incentivesHeaders: [
        { value: 'startDate', text: 'Desde', display: this.date },
        { value: 'endDate', text: 'Hasta', display: this.date },
        { value: 'campaignName', text: 'Campaña', width: 250, },
        { value: 'kpiName', text: 'KPI', width: 250, },
        { value: 'kpiType', text: 'Tipo KPI', display: KpiTypes },
        { value: 'currentUnits', text: 'Unidades vendidas' },
        { value: 'incentive', text: 'Incentivo / unidad' },
        { value: 'total', text: 'Total' }
      ],
      // Incidents of the month on screen, loaded once per month and reused to colour the days.
      incidents: [],
      incidentsKey: null,
      incidentsDialog: false,
      incidentsFilter: null,
      // The answer of a closing that is waiting for its incidents to be accepted.
      closingReview: null,
      closing: false,
      incidentHeaders: [
        { value: 'date', text: 'Fecha', display: this.date, width: 90 },
        { value: 'userName', text: 'Persona', width: 170 },
        { value: 'campaignName', text: 'Campaña', width: 170 },
        { value: 'placeName', text: 'Centro', width: 150 },
        { value: 'typesText', text: 'Incidencias', width: 170 },
        { value: 'recordedText', text: 'Registrado', width: 120 },
        { value: 'validatedText', text: 'Validado', width: 140 },
        { value: 'frozenText', text: 'Tarifas de la jornada', width: 245 },
        { value: 'placeText', text: 'Tarifas de la asignación', width: 245 }
      ]
    };
  },
  computed: {
    ...mapState([ 'core' ]),
    payrollId() {
        return this.core ? this.core.header ? this.core.header.id : 0 : 0;
      },
    payrollDate() {
      return this.core ? this.core.header ? moment(this.core.header.date).format("DD/MM/YYYY") : null : null;
    },
    payrollTotal() {
      return this.core ? this.core.header ? this.core.header.total : null : null;
    },
    isOpen() {
      return ! ( this.payrollId > 0 );
    },
    // Only the most recent month closed can be reopened, and only by a PayrollAdmin. The server
    // checks both again: this only decides whether to offer it.
    canUnlock() {
      const header = this.core ? this.core.header : null;
      return this.$store.state.isPayrollAdmin && this.payrollId > 0 && !! ( header && header.lastClosed );
    },
    incidentSummary() {
      return this.core && this.core.header ? this.core.header.incidents : null;
    },
    // One chip per kind of incident that has days. A closed month only shows the days that were
    // never validated: the rest describe what closing it would do, and it is already closed.
    incidentChips() {
      const summary = this.incidentSummary;
      if ( ! summary ) return [];

      const chips = [];
      if ( summary.notValidated > 0 ) {
        chips.push({
          type: 'NOT_VALIDATED',
          color: INCIDENTS.NOT_VALIDATED.color,
          text: `Sin validar: ${ summary.notValidated } (${ summary.notValidatedWithActivity } con actividad)`
        });
      }
      if ( this.isOpen ) {
        INCIDENT_ORDER.filter( type => type !== 'NOT_VALIDATED' ).forEach( type => {
          const count = summary[ INCIDENTS[ type ].count ];
          if ( count > 0 ) {
            chips.push({ type, color: INCIDENTS[ type ].color, text: `${ INCIDENTS[ type ].label }: ${ count }` });
          }
        });
      }
      return chips;
    },
    incidentSource() {
      if ( this.closingReview ) return this.closingReview.items || [];
      if ( this.isOpen ) return this.incidents;
      return this.incidents.filter( incident => incident.types.includes( 'NOT_VALIDATED' ));
    },
    incidentFilterChips() {
      const items = this.incidentSource;
      const chips = [{ type: null, color: 'grey lighten-2', text: `Todas: ${ items.length }` }];
      INCIDENT_ORDER.forEach( type => {
        const count = items.filter( incident => incident.types.includes( type )).length;
        if ( count > 0 ) {
          chips.push({ type, color: INCIDENTS[ type ].color, text: `${ INCIDENTS[ type ].label }: ${ count }` });
        }
      });
      return chips;
    },
    incidentRows() {
      return this.incidentSource
        .filter( incident => ! this.incidentsFilter || incident.types.includes( this.incidentsFilter ))
        .map( incident => ({
          ...incident,
          typesText: incident.types.map( type => INCIDENTS[ type ] ? INCIDENTS[ type ].label : type ).join( ', ' )
            + ( incident.activity ? ` (${ ACTIVITY[ incident.activity ] || incident.activity })` : '' ),
          recordedText: `${ this.hours( incident.workingTime ) } · ${ incident.visits } visitas`,
          validatedText: incident.validated
            ? `${ this.hours( incident.validatedWorkingTime ) } · ${ incident.validatedVisits } visitas`
              + ( incident.holiday ? ' · festivo' : '' )
            : '—',
          frozenText: incident.validated
            ? this.rates( incident.costHour, incident.holidayCostHour, incident.extraCostDay,
              incident.costVisit, incident.paidHolidays )
            : '—',
          // A rate of zero is frozen as empty, so it is shown the same way here.
          placeText: incident.idCampaignPlace
            ? this.rates( incident.placeCostHour || null, incident.placeHolidayCostHour || null,
              incident.placeExtraCostDay || null, incident.placeCostVisit || null, incident.placePaidHolidays )
            : 'Sin asignación'
        }));
    },
    // The incidents of each day, to colour the days of a payroll line.
    incidentsById() {
      const byId = {};
      this.incidents.forEach( incident => { byId[ incident.id ] = incident; });
      return byId;
    },
    actions() {
      // 'custom' es el botón Bloquear. En un mes ya cerrado la petición no haría nada
      // (el servidor devuelve las nóminas guardadas), así que se oculta para no avisar
      // de un bloqueo que no va a ocurrir.
      return this.payrollId > 0 ? "export" : "export, custom";
    },
    params() {
      return {
        month: this.month,
        grouped: this.groupByCampaign
      };
    },
    headers() {

      var headers = [];

      headers.push({
          text: "NOMBRE",
          value: "user",
          display: (a) => a.surname + ", " + a.name,
          filterable: false,
        });

      if ( this.groupByCampaign ) {
        headers.push({
          text: "CAMPAÑA",
          value: "campaignName",
          filterable: false,
        });
      }

      headers.push({
          text: "NUM. JORNADAS",
          value: "workerCount",
          display: Number,
          filterable: false,
        });

      headers.push({
          text: "NUM. INCENTIVOS",
          value: "incentiveCount",
          display: Number,
          filterable: false,
        });

      headers.push({
          text: "699 - FORMULA (I)",
          value: "cost",
          display: Number,
          filterable: false,
        });

      headers.push({
          text: "697 - FORMULA (I)",
          value: "extraCost",
          display: Number,
          filterable: false,
        });
      
        headers.push({
          text: "197 – Vacaciones",
          value: "paidHolidays",
          display: Number,
          filterable: false,
        });

        headers.push({
          text: "032 - Com.Brut (I)",
          value: "subTotal",
          display: Number,
          filterable: false,
        });

        headers.push({
          text: "012 - Plus fes (I)",
          value: "holidayCost",
          display: Number,
          filterable: false,
        });

        headers.push({
          text: "INCENTIVOS BRUTOS",
          value: "incentive",
          display: Number,
          filterable: false,
        });

        // Only while the month is open: a closed month shows what was paid, not what to review.
        if ( this.isOpen ) {
          headers.push({
            text: "INCIDENCIAS",
            value: "missingRateDays",
            display: ( value, header, item ) => this.payslipIncidents( item ),
            filterable: false,
          });
        }

        headers.push({
          text: "TOTAL",
          value: "total",
          display: Number,
          filterable: false,
        });


      return headers;
    },
  },
  watch: {
    $route() {
      this.$nextTick( this.setTitle );
    } 
  },
  methods: {
    ...mapMutations( 'app', [ 'setDialog' ]),
    initialize( params ) {
      // The month may have changed since the incidents were read: read them again when needed.
      this.incidentsKey = null;
      this.incidents = [];
      params.filters = ( params.filters || [] )
        .concat([{ field: 'date', rangeValue:{ start: moment(this.month).startOf('month').valueOf(), 
          end: moment(this.month).endOf('month').valueOf() }},
          { field: 'grouped', booleanValue: this.groupByCampaign }]);

      return this.$store.dispatch( 'page', params );
    },
    date( value ) {
        if ( value ) {
          return formatDate( value, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
          });
        }
        return "";
      },
    exportData( params ) {
      if (params === 'workerLines') {
        params = { service: 'Payroll', method: 'exportWorkerLines' };
        params.filters = ( params.filters || [] )
          .concat([{ field: 'id', stringValue: this.item.id }]);
        
        return new Promise(( resolve, reject ) => {
          Promise.resolve(this.$store.dispatch( 'downloadData', params ))
          .then( resolve )
          .catch( err => {
            this.$store.dispatch( 'console/error', err );
            reject( err );
          });
        });
      }
      else if (params === 'incentiveLines') {
        params = { service: 'Payroll', method: 'exportIncentiveLines' };
        params.filters = ( params.filters || [] )
          .concat([{ field: 'id', stringValue: this.item.id }]);
        
        return new Promise(( resolve, reject ) => {
          Promise.resolve(this.$store.dispatch( 'downloadData', params ))
          .then( resolve )
          .catch( err => {
            this.$store.dispatch( 'console/error', err );
            reject( err );
          });
        });
      }
      else {
        params.filters = ( params.filters ||  [])
          .concat([{ field: 'date', rangeValue:{ start: moment(this.month).startOf('month').valueOf(), 
            end: moment(this.month).endOf('month').valueOf() }},
            { field: 'grouped', booleanValue: this.groupByCampaign }]);

        return this.$store.dispatch( 'downloadData', params );
      }
    },
    setTitle() {
      const title = 'Nómina';
      return this.$store.commit( 'setTitle', title );
    },
    showWorkerLines( worker ) {
      // To colour the days that have an incident.
      this.loadIncidents();
      this.dialog = true;
      this.incentiveDialog = false;
      this.item = worker;
      const target = "Payroll/getWorkerCosts";
      const params = { id: worker.id };

      this.$store
        .dispatch("api", { target, params })
        .then( response => {
          this.workersList = response.data;
        })
        .catch( err => this.$store.dispatch( 'console/error', err ));
    },
     showIncentiveLines( worker ) {
      this.dialog = true;
      this.incentiveDialog = true;
      this.item = worker;
      const target = "Payroll/getIncentiveCosts";
      const params = { id: worker.id };

      this.$store
        .dispatch("api", { target, params })
        .then( response => {
          this.incentivesList = response.data;
        })
        .catch( err => this.$store.dispatch( 'console/error', err ));
    },
    rowClass( classes, item ) {
      return {
        ...classes,
        'worker-active': item.active
      };
    },
    closeDialog() {
      this.dialog = false;
      this.workersList = [];
      this.incentivesList = [];
      this.item = null;
    },
    lockPayroll() {
      this.setDialog({
            show: true,
            width: 400,
            title: 'ALERTA',
            text: '<p>Está a punto de bloquear la nómina de este mes.</p>'
                + '<p class="mb-0"><strong>¿Está seguro que desea continuar?</strong></p>',
            acceptText: 'Continuar',
            accept: () => this.addPayroll()
          });
    },
    // Closes the month reviewing its incidents first. With none, the server closes it straight
    // away. Otherwise it answers with them and closes nothing until the person closing it accepts
    // exactly those, which is what the fingerprint sent back says.
    addPayroll( accepted ) {
      var parms = { service: 'Payroll', method: 'add' };
      parms.filters = [
        { field: 'date', rangeValue: this.monthRange() },
        { field: 'checkIncidents', booleanValue: true }
      ];
      if ( accepted ) {
        parms.filters.push({ field: 'acceptIncidents', stringValue: accepted });
      }

      this.closing = true;
      return Promise.resolve( this.$store.dispatch( 'exportData', parms ))
        .then( result => {
          if ( result && result.closed ) {
            this.closeIncidents();
            this.reload();
            return;
          }
          this.closingReview = result;
          this.incidentsFilter = null;
          this.incidentsDialog = true;
        })
        .catch( err => this.showError( err, 'No se ha podido bloquear la nómina' ))
        .finally(() => { this.closing = false; });
    },
    unlockPayroll() {
      if ( ! this.canUnlock ) return;

      const header = this.core.header;
      const month = moment({ year: header.year, month: header.month - 1, day: 1 });
      const name = `${ MONTH_NAMES[ month.month() ] } de ${ month.year() }`;
      const total = Number( header.total || 0 ).toLocaleString( 'es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      this.setDialog({
        show: true,
        width: 460,
        title: 'ALERTA',
        text: `<p>Está a punto de desbloquear la nómina de <strong>${ name }</strong>,`
          + ` con un importe total de <strong>${ total } €</strong>.</p>`
          + '<p>Se borrarán sus líneas, y las jornadas e incentivos del mes volverán a poder modificarse.'
          + ' Para cerrarla otra vez habrá que volver a bloquearla.</p>'
          + '<p class="mb-0"><strong>¿Está seguro que desea continuar?</strong></p>',
        acceptText: 'Desbloquear',
        accept: () => this.doUnlock( header.id )
      });
    },
    doUnlock( id ) {
      return this.$store
        .dispatch( 'api', { target: 'Payroll/unlock', params: { id }})
        .then(() => this.reload())
        .catch( err => this.showError( err, 'No se ha podido desbloquear la nómina' ));
    },
    acceptIncidents() {
      if ( this.closingReview && this.closingReview.incidents ) {
        this.addPayroll( this.closingReview.incidents.fingerprint );
      }
    },
    monthRange() {
      return {
        start: moment( this.month ).startOf( 'month' ).valueOf(),
        end: moment( this.month ).endOf( 'month' ).valueOf()
      };
    },
    loadIncidents() {
      const range = this.monthRange();
      const key = range.start + '_' + range.end;
      if ( this.incidentsKey === key ) return Promise.resolve( this.incidents );

      this.incidentsKey = key;
      return this.$store
        .dispatch( 'api', { target: 'Payroll/getIncidents', params: range })
        .then( response => {
          // Ignore an answer for a month that is no longer on screen.
          if ( this.incidentsKey === key ) this.incidents = response.data;
          return this.incidents;
        })
        .catch( err => {
          this.incidentsKey = null;
          this.$store.dispatch( 'console/error', err );
        });
    },
    openIncidents( type ) {
      this.closingReview = null;
      this.incidentsFilter = type || null;
      this.incidentsDialog = true;
      this.loadIncidents();
    },
    closeIncidents() {
      this.incidentsDialog = false;
      this.closingReview = null;
      this.incidentsFilter = null;
    },
    showError( err, fallback ) {
      this.$store.dispatch( 'console/error', err );
      const message = err && err.data && typeof err.data.object === 'string' ? err.data.object : fallback;
      const escaped = String( message ).replace( /&/g, '&amp;' ).replace( /</g, '&lt;' ).replace( />/g, '&gt;' );
      this.setDialog({
        show: true,
        width: 400,
        title: 'ERROR',
        text: `<p class="mb-0">${ escaped }</p>`,
        acceptText: 'Aceptar'
      });
    },
    classFor( types ) {
      const type = INCIDENT_ORDER.find( t => types.includes( t ));
      return type ? INCIDENTS[ type ].cls : null;
    },
    payslipTypes( item ) {
      const types = [];
      if ( item.missingRateDays > 0 ) types.push( 'MISSING_RATE' );
      if ( item.rateChangedDays > 0 ) types.push( 'RATE_CHANGED' );
      if ( item.notStampedDays > 0 ) types.push( 'NOT_STAMPED' );
      if ( item.validatedAtZeroDays > 0 ) types.push( 'VALIDATED_AT_ZERO' );
      return types;
    },
    payslipIncidents( item ) {
      const counts = {
        MISSING_RATE: item.missingRateDays,
        RATE_CHANGED: item.rateChangedDays,
        NOT_STAMPED: item.notStampedDays,
        VALIDATED_AT_ZERO: item.validatedAtZeroDays
      };
      return this.payslipTypes( item )
        .map( type => `${ INCIDENTS[ type ].label } ${ counts[ type ] }` )
        .join( ' · ' );
    },
    payslipRowClass( classes, item ) {
      const cls = this.isOpen && item ? this.classFor( this.payslipTypes( item )) : null;
      return cls ? { ...classes, [ cls ]: true } : classes;
    },
    workerRowClass( classes, item ) {
      const base = this.rowClass( classes, item );
      const incident = this.isOpen && item ? this.incidentsById[ item.id ] : null;
      const cls = incident ? this.classFor( incident.types ) : null;
      return cls ? { ...base, [ cls ]: true } : base;
    },
    incidentRowClass( classes, item ) {
      const cls = this.classFor( item.types || [] );
      return cls ? { ...classes, [ cls ]: true } : classes;
    },
    // The list as it is on screen, with the filter that is on, built here rather than on the
    // server: the rows are already loaded, and a server export would leave a file with names
    // and rates in the public export folder.
    exportIncidents() {
      const rows = [ this.incidentHeaders.map( header => header.text ) ]
        .concat( this.incidentRows.map( row => this.incidentHeaders.map( header =>
          header.display ? header.display( row[ header.value ], header, row ) : row[ header.value ])));

      const month = moment( this.month );
      const filter = this.incidentsFilter && INCIDENTS[ this.incidentsFilter ]
        ? `-${ INCIDENTS[ this.incidentsFilter ].label.toLowerCase().replace( / /g, '-' )}`
        : '';

      downloadCsv( rows, `incidencias-nomina-${ MONTH_NAMES[ month.month() ]}-${ month.year() }${ filter }.csv` );
    },
    hours( minutes ) {
      const total = Number( minutes ) || 0;
      return `${ Math.floor( total / 60 ) }:${ String( total % 60 ).padStart( 2, '0' ) } h`;
    },
    money( value ) {
      return value === null || value === undefined
        ? '—'
        : Number( value ).toLocaleString( 'es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },
    rates( hour, holiday, extra, visit, paidHolidays ) {
      const paid = paidHolidays === true ? 'sí' : paidHolidays === false ? 'no' : '—';
      return `${ this.money( hour ) } €/h · festivo ${ this.money( holiday ) } · fijo ${ this.money( extra ) }`
        + ` · visita ${ this.money( visit ) } · vacaciones ${ paid }`;
    },
    reload() {
        const { table } = this.$refs;
        table && table.reload();
      },
  },
  mounted() {
    this.setTitle();
  }
};

// The same separator as the CSV files of the server, so that Excel in Spanish opens them in
// columns, and a byte order mark so that it reads the accents as UTF-8.
function downloadCsv( rows, fileName ) {
  const text = '\uFEFF' + rows.map( row => row.map( csvValue ).join( ';' )).join( '\r\n' );
  saveFile( new Blob([ text ], { type: 'text/csv;charset=utf-8' }), fileName );
}

function csvValue( value ) {
  const text = value === null || value === undefined ? '' : String( value );
  return /[;"\r\n]/.test( text ) ? `"${ text.replace( /"/g, '""' )}"` : text;
}
</script>

<style>
.v-dialog__content {
  justify-content: normal;
}
.workers-dialog {
  width: 100%;
  min-height: 350px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.workers-dialog > .workers-dialog-body {
  flex: 1 1 auto;
  position: relative;
}
.workers-dialog > .workers-dialog-body > div {
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  overflow: auto;
}
.workers-dialog .dtable-header > div {
  background-color: var(--v-tertiary-base);
}
.workers-dialog .dtable-row:not(.worker-active),
.workers-dialog .dtable-row:not(.worker-active):hover {
  background-color: #eee;
}
.incentives-dialog {
  width: 100%;
  min-height: 350px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.incentives-dialog > .incentives-dialog-body {
  flex: 1 1 auto;
  position: relative;
}
.incentives-dialog > .incentives-dialog-body > div {
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  overflow: auto;
}
.incentives-dialog .dtable-header > div {
  background-color: var(--v-tertiary-base);
}
.incentives-dialog .dtable-row:not(.worker-active),
.incentives-dialog .dtable-row:not(.worker-active):hover {
  background-color: #eee;
}
.incidents-list-dialog {
  width: 100%;
  min-height: 350px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.incidents-list-dialog > .incidents-list-dialog-body {
  flex: 1 1 auto;
  position: relative;
  min-height: 300px;
}
.incidents-list-dialog > .incidents-list-dialog-body > div {
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  overflow: auto;
}
.incidents-list-dialog .dtable-header > div {
  background-color: var(--v-tertiary-base);
}
/* The same colours as the chips. Repeated with the dialogs' prefixes so they win over the grey
   those give to every row. */
.dtable-row.incident-missing-rate,
.workers-dialog .dtable-row.incident-missing-rate,
.incidents-list-dialog .dtable-row.incident-missing-rate { background-color: #ffcdd2; }
.dtable-row.incident-rate-changed,
.workers-dialog .dtable-row.incident-rate-changed,
.incidents-list-dialog .dtable-row.incident-rate-changed { background-color: #ffe0b2; }
.dtable-row.incident-not-stamped,
.workers-dialog .dtable-row.incident-not-stamped,
.incidents-list-dialog .dtable-row.incident-not-stamped { background-color: #bbdefb; }
.dtable-row.incident-validated-zero,
.workers-dialog .dtable-row.incident-validated-zero,
.incidents-list-dialog .dtable-row.incident-validated-zero { background-color: #fff59d; }
.dtable-row.incident-not-validated,
.workers-dialog .dtable-row.incident-not-validated,
.incidents-list-dialog .dtable-row.incident-not-validated { background-color: #e1bee7; }
</style>

